import { useState, useEffect, useCallback } from 'react';
import { TopBar } from '../components/layout/TopBar';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { CreditCard, DollarSign, Clock, Shield, Loader2, Save, TrendingUp, RefreshCw, Wallet } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

export default function Deposits() {
  const [config, setConfig] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('config');

  /* OkWallet tab state */
  const [walletInfo, setWalletInfo] = useState(null);
  const [walletTxs, setWalletTxs] = useState([]);
  const [walletLoading, setWalletLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cR, dR] = await Promise.all([api.get('/deposits/config'), api.get('/deposits')]);
      setConfig(cR.data);
      setDeposits(Array.isArray(dR.data) ? dR.data : []);
    } catch { toast.error('Error al cargar'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async () => {
    setSaving(true);
    try { await api.patch('/deposits/config', config); toast.success('Configuración guardada'); }
    catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  /* OkWallet helpers */
  const fetchWallet = useCallback(async () => {
    setWalletLoading(true);
    try {
      const [wRes, txRes] = await Promise.all([
        fetch(`${API_URL}/api/wallet/me`, { headers: authHeader() }),
        fetch(`${API_URL}/api/wallet/transactions?limit=30`, { headers: authHeader() }),
      ]);
      if (wRes.ok) setWalletInfo(await wRes.json());
      if (txRes.ok) {
        const data = await txRes.json();
        setWalletTxs(Array.isArray(data) ? data : (Array.isArray(data?.transactions) ? data.transactions : []));
      }
    } catch { toast.error('Error al cargar cartera'); }
    finally { setWalletLoading(false); }
  }, []);

  const totalCollected = deposits.filter(d => d.status === 'paid').reduce((s, d) => s + (d.amount || 0), 0);
  const pendingDeposits = deposits.filter(d => d.status === 'pending').length;

  if (loading) return (
    <div className="h-screen flex flex-col"><TopBar title="Depósitos y Prepagos" helpSection="deposits" />
      <div className="flex-1 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <TopBar title="Depósitos y Prepagos" helpSection="deposits" actions={
        tab === 'config' ? (
          <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white h-8">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />} Guardar
          </Button>
        ) : undefined
      } />

      <div className="flex-1 p-6 max-w-4xl mx-auto w-full space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-green-600 flex items-center justify-center">
              <CreditCard className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Depósitos y Prepagos</h1>
              <p className="text-sm text-slate-500">Recoge depósitos para grupos grandes y eventos especiales</p>
            </div>
          </div>
          {config && (
            <div className="flex items-center gap-3">
              <Label className="text-sm text-slate-600">Sistema activo</Label>
              <Switch checked={!!config?.enabled} onCheckedChange={v => setConfig(c => ({ ...c, enabled: v }))} />
            </div>
          )}
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { l: 'Total recaudado', v: `$${totalCollected}`, icon: DollarSign, c: 'text-green-600', bg: 'bg-green-50' },
            { l: 'Depósitos pendientes', v: pendingDeposits, icon: Clock, c: 'text-amber-600', bg: 'bg-amber-50' },
            { l: 'Depósitos totales', v: deposits.length, icon: TrendingUp, c: 'text-blue-600', bg: 'bg-blue-50' },
          ].map(s => (
            <Card key={s.l} className="border-0 shadow-sm">
              <CardContent className="p-5">
                <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center mb-3", s.bg)}>
                  <s.icon className={cn("h-5 w-5", s.c)} />
                </div>
                <div className="text-3xl font-black text-slate-800">{s.v}</div>
                <div className="text-xs text-slate-500 mt-0.5">{s.l}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
          {[
            { k: 'config', l: 'Configuración' },
            { k: 'deposits', l: 'Depósitos' },
            { k: 'wallet', l: 'OkWallet' },
          ].map(t => (
            <button key={t.k} onClick={() => { setTab(t.k); if (t.k === 'wallet') fetchWallet(); }}
              className={cn("px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5",
                tab === t.k ? "bg-white shadow-sm text-slate-800" : "text-slate-500 hover:text-slate-700")}>
              {t.k === 'wallet' && <Wallet className="h-3.5 w-3.5" />}
              {t.l}
            </button>
          ))}
        </div>

        {/* CONFIG TAB */}
        {tab === 'config' && config && (
          <div className="grid grid-cols-2 gap-6">
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="text-sm">Reglas de depósito</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Tamaño mínimo del grupo</Label>
                  <div className="flex items-center gap-2">
                    <Input type="number" min={1} value={config.min_party_size}
                      onChange={e => setConfig(c => ({ ...c, min_party_size: +e.target.value }))} className="text-sm max-w-[100px]" />
                    <span className="text-sm text-slate-500">personas o más</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-2 block">Tipo de depósito</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => setConfig(c => ({ ...c, amount_type: 'fixed' }))}
                      className={cn("py-2 px-3 rounded-xl border text-sm font-medium transition-all",
                        config.amount_type === 'fixed' ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600 hover:border-slate-300")}>
                      $ Cantidad fija
                    </button>
                    <button onClick={() => setConfig(c => ({ ...c, amount_type: 'percentage' }))}
                      className={cn("py-2 px-3 rounded-xl border text-sm font-medium transition-all",
                        config.amount_type === 'percentage' ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600 hover:border-slate-300")}>
                      % Porcentaje
                    </button>
                  </div>
                </div>
                {config.amount_type === 'fixed' ? (
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Cantidad por persona</Label>
                    <Input type="number" min={0} value={config.amount}
                      onChange={e => setConfig(c => ({ ...c, amount: +e.target.value }))} className="text-sm max-w-[120px]" />
                  </div>
                ) : (
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Porcentaje del total estimado (%)</Label>
                    <Input type="number" min={0} max={100} value={config.percentage}
                      onChange={e => setConfig(c => ({ ...c, percentage: +e.target.value }))} className="text-sm max-w-[120px]" />
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><Shield className="h-4 w-4 text-green-600" /> Política de reembolso</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Depósito reembolsable</Label>
                    <p className="text-xs text-slate-400 mt-0.5">Si el cliente cancela con suficiente antelación</p>
                  </div>
                  <Switch checked={!!config.refundable} onCheckedChange={v => setConfig(c => ({ ...c, refundable: v }))} />
                </div>
                {config.refundable && (
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Horas mínimas de antelación para reembolso</Label>
                    <div className="flex items-center gap-2">
                      <Input type="number" min={0} value={config.refund_hours_before}
                        onChange={e => setConfig(c => ({ ...c, refund_hours_before: +e.target.value }))} className="text-sm max-w-[100px]" />
                      <span className="text-sm text-slate-500">horas antes</span>
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Descripción de la política</Label>
                  <textarea value={config.description} onChange={e => setConfig(c => ({ ...c, description: e.target.value }))}
                    rows={3} className="w-full text-xs border border-input rounded-xl px-3 py-2 resize-none outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500" />
                </div>
                <div className="p-3 bg-slate-50 rounded-xl text-xs text-slate-600">
                  <strong>Resumen de tu política:</strong><br />
                  {config.enabled
                    ? `Se solicitará un depósito de ${config.amount_type === 'fixed' ? `$${config.amount}/persona` : `${config.percentage}%`} para grupos de ${config.min_party_size}+ personas. ${config.refundable ? `Reembolsable si se cancela con ${config.refund_hours_before}h de antelación.` : 'No reembolsable.'}`
                    : 'Sistema de depósitos desactivado.'}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* DEPOSITS TAB */}
        {tab === 'deposits' && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {deposits.length === 0 ? (
                <div className="text-center py-16 text-slate-400">
                  <CreditCard className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">Sin depósitos registrados</p>
                  <p className="text-sm mt-1">Los depósitos de reservas aparecerán aquí</p>
                </div>
              ) : (
                <div className="divide-y">
                  {deposits.map((d, i) => (
                    <div key={i} className="flex items-center gap-4 px-5 py-3 hover:bg-slate-50">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-800">{d.guest_name || 'Cliente'}</p>
                        <p className="text-xs text-slate-400">{d.reservation_date} · {d.party_size} personas</p>
                      </div>
                      <span className="text-sm font-bold text-slate-800">${d.amount}</span>
                      <span className={cn("text-[10px] px-2 py-0.5 rounded-full font-semibold",
                        d.status === 'paid' ? 'bg-green-100 text-green-700' : d.status === 'refunded' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700')}>
                        {d.status === 'paid' ? 'Pagado' : d.status === 'refunded' ? 'Reembolsado' : 'Pendiente'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* OKWALLET TAB */}
        {tab === 'wallet' && (
          <div className="space-y-5">
            {walletLoading ? (
              <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>
            ) : (
              <>
                {/* Wallet balance card */}
                <div className="bg-gradient-to-br from-indigo-600 to-indigo-800 text-white rounded-2xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-indigo-200 text-sm mb-1">Saldo OkWallet</p>
                      <p className="text-4xl font-black">${(walletInfo?.balance || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                      <p className="text-indigo-300 text-xs mt-1 font-mono">OkWallet ID: {walletInfo?.okwallet_id || walletInfo?.id || '—'}</p>
                    </div>
                    <div className="text-right">
                      <Wallet className="h-12 w-12 text-indigo-300 mb-2" />
                      <span className={cn("text-xs px-2 py-0.5 rounded-full font-semibold",
                        walletInfo?.status === 'active' ? 'bg-green-400/20 text-green-200' : 'bg-red-400/20 text-red-200')}>
                        {walletInfo?.status === 'active' ? 'Activa' : walletInfo?.status || 'Inactiva'}
                      </span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-indigo-500/40 grid grid-cols-3 gap-4 text-center">
                    <div><p className="text-xl font-bold">${(walletInfo?.total_received || 0).toLocaleString()}</p><p className="text-indigo-300 text-xs">Recibido</p></div>
                    <div><p className="text-xl font-bold">${(walletInfo?.total_sent || 0).toLocaleString()}</p><p className="text-indigo-300 text-xs">Enviado</p></div>
                    <div><p className="text-xl font-bold">${(walletInfo?.total_recharged || 0).toLocaleString()}</p><p className="text-indigo-300 text-xs">Recargado</p></div>
                  </div>
                </div>

                {/* Enable OkWallet deposits toggle */}
                {config && (
                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-slate-800 text-sm">Aceptar depósitos vía OkWallet</p>
                        <p className="text-xs text-slate-500 mt-0.5">Los clientes con OkWallet podrán pagar depósitos directamente desde su cartera</p>
                      </div>
                      <Switch
                        checked={!!config.accept_wallet}
                        onCheckedChange={v => setConfig(c => ({ ...c, accept_wallet: v }))}
                      />
                    </CardContent>
                  </Card>
                )}

                {/* Transactions */}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center justify-between">
                      Movimientos recientes
                      <Button size="sm" variant="ghost" onClick={fetchWallet} className="h-7 text-xs gap-1">
                        <RefreshCw className="h-3 w-3" /> Actualizar
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {walletTxs.length === 0 ? (
                      <div className="text-center py-10 text-slate-400">
                        <Wallet className="h-8 w-8 mx-auto mb-2 opacity-20" />
                        <p className="text-sm">Sin movimientos todavía</p>
                      </div>
                    ) : (
                      <div className="divide-y max-h-80 overflow-y-auto">
                        {walletTxs.map((tx, i) => (
                          <div key={i} className="flex items-center gap-3 px-5 py-2.5 hover:bg-slate-50">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-slate-700">{tx.description || tx.category || tx.type}</p>
                              <p className="text-[11px] text-slate-400">{tx.created_at?.slice(0, 10)}</p>
                            </div>
                            <span className={cn("text-sm font-bold",
                              tx.type === 'debit' || tx.category === 'transfer_out' ? 'text-red-600' : 'text-green-600')}>
                              {tx.type === 'debit' || tx.category === 'transfer_out' ? '-' : '+'}${(tx.amount || 0).toLocaleString()}
                            </span>
                            <span className={cn("text-[10px] px-2 py-0.5 rounded-full font-semibold",
                              tx.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700')}>
                              {tx.status === 'completed' ? 'Completado' : tx.status || 'Pendiente'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
